/**
 * Schedule hub — the sidebar "Schedule" page.
 *
 * One place that answers "where do I create games?" for every competition:
 *  - each TOURNAMENT row → "Open calendar" → the Day2 tournament scheduler
 *    (/admin/scheduler-day2/tournament/:id), and expands to its contests'
 *    divisions;
 *  - each standalone SEASON row → "Open scheduler" → the season scheduler
 *    (/admin/organization/scheduler/:leagueId), and expands to its divisions;
 *  - every division links to DivisionPage (/admin/division/:id) — the
 *    canonical per-division game surface (Create Game, Auto-Populate
 *    round-robin, pools, brackets).
 *
 * The schedulers build their grids from venue-availability TimeSlots, so with
 * none the grid renders empty with no explanation — the hub surfaces that as
 * an explicit hint pointing at /admin/locations.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { CalendarDays, ChevronDown, ChevronRight, Plus, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSportLabel } from '@staty/shared/utils/sportLabels';
import useTournamentStore from '@store/tournamentStore';
import useLeagueStore from '@store/leagueStore';
import useDivisionStore from '@store/divisionStore';
import useTimeSlotStore from '@store/timeSlotStore';
import useOrganizationStore from '@store/organizationStore';
import useSummaryStore from '@store/summaryStore';
import helpers from '@/utils/helpers';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import QuickAvailabilityWizard, { type WizardCompetition } from '@/components/QuickAvailabilityWizard/QuickAvailabilityWizard';

type Ref = string | { _id?: string } | null | undefined;

interface NamedDoc {
  _id: string;
  name?: string;
  active?: boolean;
  tournament?: Ref;
  league?: Ref;
}

// Several legacy stores are `@ts-nocheck`, so their hooks type as `unknown` —
// these are the slices this page reads, asserted at the boundary.
interface HubStores {
  tournaments: NamedDoc[];
  getTournamentsByLeagueName: (args: { leagueName: string }) => Promise<void>;
  leagues: NamedDoc[];
  getLeaguesByOrganizationName: (name: string, opts?: { contestType?: string }) => Promise<void>;
  timeSlots: Array<{ tournament?: Ref; league?: Ref }>;
  getTimeSlotsByLeagueName: (name: string) => Promise<void>;
  organizations: Array<{ name?: string; sports?: string[] }>;
}

/** Manual `active` flags first (default true = not archived), then by name. */
function sortRows<T extends NamedDoc>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const activeDelta = Number(b.active !== false) - Number(a.active !== false);
    if (activeDelta !== 0) return activeDelta;
    return (a.name || '').localeCompare(b.name || '');
  });
}

function DivisionLinks({ divisions }: { divisions: NamedDoc[] }) {
  if (!divisions.length) {
    return (
      <p className="text-sm text-muted-foreground px-4 py-2">
        No divisions yet. Add them in{' '}
        <Link to="/admin/organize" className="underline">Competitions</Link>.
      </p>
    );
  }
  return (
    <div className="divide-y">
      {divisions.map((division) => (
        <Link
          key={division._id}
          to={`/admin/division/${division._id}`}
          className="flex items-center justify-between pl-10 pr-4 py-2 text-sm hover:bg-muted/50 transition-colors"
        >
          <span>{division.name}</span>
          <span className="text-xs text-muted-foreground">Games &amp; brackets →</span>
        </Link>
      ))}
    </div>
  );
}

function NoSlotsChip() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-warning/10 text-warning border border-warning/30 px-2 py-0.5 text-xs"
      title="The scheduler's day grid is built from venue availability. Add it in Locations"
    >
      <TriangleAlert className="w-3 h-3" />
      No time slots
    </span>
  );
}

export default function SchedulerListAdmin() {
  const { orgSlug, getToken } = useAuth();
  const { tournaments, getTournamentsByLeagueName } =
    (useTournamentStore() as unknown) as Pick<HubStores, 'tournaments' | 'getTournamentsByLeagueName'>;
  const { leagues, getLeaguesByOrganizationName } =
    (useLeagueStore() as unknown) as Pick<HubStores, 'leagues' | 'getLeaguesByOrganizationName'>;
  const { divisions, getDivisionsByLeagueName } = useDivisionStore();
  const { timeSlots, getTimeSlotsByLeagueName } =
    (useTimeSlotStore() as unknown) as Pick<HubStores, 'timeSlots' | 'getTimeSlotsByLeagueName'>;
  const { organizations } = (useOrganizationStore() as unknown) as Pick<HubStores, 'organizations'>;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [slotsLoaded, setSlotsLoaded] = useState(false);

  useEffect(() => {
    if (!orgSlug) return;
    getTournamentsByLeagueName({ leagueName: orgSlug });
    // ALL leagues (contests included) so tournament rows can group divisions
    // by contest; the seasons section filters `!league.tournament` at render.
    getLeaguesByOrganizationName(orgSlug);
    getDivisionsByLeagueName(orgSlug);
    // Org-wide fetch REPLACES the store's timeSlots — call exactly once here.
    getTimeSlotsByLeagueName(orgSlug).then(() => setSlotsLoaded(true));
  }, [orgSlug]);

  const organization = organizations.find((o) => o.name === orgSlug);
  const venuesLabel = getSportLabel(organization?.sports?.[0], 'venues');

  // INT-0027: deep-link target for "Create your first game" — any division
  // works; the quick-create modal is division-scoped.
  const firstDivisionId = helpers.getObjId(((divisions as unknown) as NamedDoc[])[0]);

  const divisionsByLeague = useMemo(() => {
    const map = new Map<string, NamedDoc[]>();
    for (const division of (divisions as unknown) as NamedDoc[]) {
      const leagueId = helpers.getObjId(division.league);
      if (!leagueId) continue;
      if (!map.has(leagueId)) map.set(leagueId, []);
      map.get(leagueId)!.push(division);
    }
    return map;
  }, [divisions]);

  const contestsByTournament = useMemo(() => {
    const map = new Map<string, NamedDoc[]>();
    for (const league of leagues) {
      const tournamentId = helpers.getObjId(league.tournament);
      if (!tournamentId) continue;
      if (!map.has(tournamentId)) map.set(tournamentId, []);
      map.get(tournamentId)!.push(league);
    }
    return map;
  }, [leagues]);

  const seasons = useMemo(
    () => sortRows(leagues.filter((league) => !league.tournament)),
    [leagues]
  );
  const sortedTournaments = useMemo(() => sortRows(tournaments), [tournaments]);

  const hasSlotsFor = (key: 'tournament' | 'league', id: string) =>
    timeSlots.some((ts) => helpers.getObjId(ts[key]) === id);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const noTimeSlots = slotsLoaded && timeSlots.length === 0;
  const noCompetitions = sortedTournaments.length === 0 && seasons.length === 0;

  // INT-0027 quick availability: one dialog creates the venue slots for a
  // competition and gives the drafted (time-less) games their times.
  const [wizardOpen, setWizardOpen] = useState(false);
  const { setup, getSetup } = useSummaryStore((s: any) => ({ setup: s.setup, getSetup: s.getSetup(getToken) }));
  const orgId: string = ((organization as unknown) as { _id?: string } | undefined)?._id || '';
  useEffect(() => { if (orgId) getSetup(orgId); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps
  const gamesWithoutTime: number = Number(setup?.gamesWithoutTime || 0);
  const wizardCompetitions: WizardCompetition[] = [
    ...seasons.map((l) => ({ kind: 'league' as const, id: l._id, name: l.name || 'Season' })),
    ...sortedTournaments.map((t) => ({ kind: 'tournament' as const, id: t._id, name: t.name || 'Tournament' })),
  ];
  const onWizardDone = () => {
    getTimeSlotsByLeagueName(orgSlug);
    if (orgId) getSetup(orgId);
  };

  return (
    <PageContainer>
      <PageHeader
        title={(
          <span className="flex items-center gap-2">
            <CalendarDays className="w-6 h-6" />
            Schedule
          </span>
        )}
        description="Open a tournament's day calendar or a season's scheduler, or jump into a division to create games, run Auto-Populate, and manage brackets."
      />

      {noTimeSlots && (
        /* INT-0027 first-run panel (replaces the old prerequisite-style
           warning): primary action = create a game (division quick-create,
           ?create=1 auto-opens the modal); availability is the UPGRADE. */
        <div className="rounded-lg border bg-card px-5 py-4 shadow-sm">
          <p className="font-medium">{gamesWithoutTime > 0 ? 'Set your game times' : 'Schedule your first game'}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {gamesWithoutTime > 0
              ? `${gamesWithoutTime} drafted game${gamesWithoutTime === 1 ? ' has' : 's have'} a date but no time yet. Tell us when you play and they get their times, and the day grid turns on.`
              : `Tell us when you play and the day grid, drag and drop, and conflict checks turn on. Or create one game by hand.`}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {wizardCompetitions.length > 0 && (
              <Button size="sm" onClick={() => setWizardOpen(true)} data-tour="scheduler-quick-availability">
                <CalendarDays className="mr-1.5 h-4 w-4" />
                {gamesWithoutTime > 0 ? 'Set game times' : 'Set up availability'}
              </Button>
            )}
            {firstDivisionId && (
              <Button asChild size="sm" variant="outline">
                <Link to={`/admin/division/${firstDivisionId}?create=1`}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Create a game by hand
                </Link>
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              Prefer to manage {venuesLabel.toLowerCase()} yourself? Add availability in{' '}
              <Link to="/admin/locations" className="text-primary underline">{venuesLabel}</Link>.
            </span>
          </div>
          <QuickAvailabilityWizard
            open={wizardOpen}
            onOpenChange={setWizardOpen}
            organization={organization}
            competitions={wizardCompetitions}
            onDone={onWizardDone}
          />
        </div>
      )}

      {noCompetitions && (
        <p className="text-sm text-muted-foreground">
          Nothing to schedule yet. Create a tournament or league in{' '}
          <Link to="/admin/organize" className="underline">Competitions</Link>.
        </p>
      )}

      {sortedTournaments.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-medium mb-3 text-muted-foreground">Tournaments</h2>
          <div className="rounded-md border divide-y">
            {sortedTournaments.map((tournament) => {
              const isOpen = expanded.has(tournament._id);
              const contests = sortRows(contestsByTournament.get(tournament._id) || []);
              return (
                <div key={tournament._id}>
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors">
                    <button
                      type="button"
                      onClick={() => toggle(tournament._id)}
                      className="flex items-center gap-2 flex-1 text-left"
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-medium">{tournament.name}</span>
                      {tournament.active === false && (
                        <span className="text-xs text-muted-foreground">(inactive)</span>
                      )}
                      {slotsLoaded && !hasSlotsFor('tournament', tournament._id) && <NoSlotsChip />}
                    </button>
                    <Link
                      to={`/admin/scheduler-day2/tournament/${tournament._id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors shrink-0"
                    >
                      <CalendarDays className="w-4 h-4" />
                      Open calendar
                    </Link>
                  </div>
                  {isOpen && (
                    <div className="border-t bg-muted/20">
                      {contests.length === 0 ? (
                        <DivisionLinks divisions={[]} />
                      ) : (
                        contests.map((contest) => (
                          <div key={contest._id}>
                            <p className="pl-10 pr-4 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              {contest.name}
                            </p>
                            <DivisionLinks divisions={divisionsByLeague.get(contest._id) || []} />
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {seasons.length > 0 && (
        <section>
          <h2 className="text-lg font-medium mb-3 text-muted-foreground">Leagues / Seasons</h2>
          <div className="rounded-md border divide-y">
            {seasons.map((league) => {
              const isOpen = expanded.has(league._id);
              return (
                <div key={league._id}>
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors">
                    <button
                      type="button"
                      onClick={() => toggle(league._id)}
                      className="flex items-center gap-2 flex-1 text-left"
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-medium">{league.name}</span>
                      {league.active === false && (
                        <span className="text-xs text-muted-foreground">(inactive)</span>
                      )}
                      {slotsLoaded && !hasSlotsFor('league', league._id) && <NoSlotsChip />}
                    </button>
                    <Link
                      to={`/admin/organization/scheduler/${league._id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors shrink-0"
                    >
                      <CalendarDays className="w-4 h-4" />
                      Open scheduler
                    </Link>
                  </div>
                  {isOpen && (
                    <div className="border-t bg-muted/20">
                      <DivisionLinks divisions={divisionsByLeague.get(league._id) || []} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </PageContainer>
  );
}
